import { Calendar, Info } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Common Label Component (without remarks - kept simple)
const InputLabel = ({ title, required, labelPlacement = "left" }) => {
  if (!title) return null;

  return (
    <label
      className={`font-medium break-words text-md text-[#575757] ${
        labelPlacement === "top" ? "mb-1" : "w-1/3 mr-1"
      }`}
    >
      <span>{title}</span>
      {required && <span className="text-red-500">*</span>}
    </label>
  );
};

const TextAreaInput = ({
  type,
  id,
  name,
  value,
  onChange,
  onBlur,
  disabled,
  className = "",
  highlightClass = "",
  minLength,
  maxLength,
  autoComplete,
  spellCheck,
  placeholder,
  title,
  rows = 1,
  charPerRow = 20,
  required,
  labelPlacement = "left",
  ...rest
}) => {
  const [numRows, setNumRows] = useState(rows);
  const textareaRef = useRef(null);

  useEffect(() => {
    let rowsNeeded = Math.max(
      Math.ceil(String(value).length / charPerRow),
      rows
    );
    setNumRows(rowsNeeded === 1 ? rowsNeeded : rowsNeeded + 1);
  }, [value, rows, charPerRow]);

  const handleBlur = (e) => {
    const input = e.target;
    const { value } = input;
    const upperValue = value;
    onChange?.({ target: { ...e.target, value: upperValue } });
    onBlur?.(e);
  };

  const handleChange = (e) => {
    const input = e.target;
    const { value } = input;
    onChange?.({ target: { ...e.target, value: value } });
  };

  const resizeTextarea = () => {
    const ref = textareaRef.current;
    if (ref) {
      ref.style.height = "auto";
      ref.style.minHeight = `${ref.scrollHeight}px`;
    }
  };

  useEffect(() => {
    resizeTextarea();
  }, [value]);

  return (
    <div
      className={`my-2 relative group ${
        labelPlacement === "top" ? "flex flex-col" : "flex items-center"
      }`}
    >
      <InputLabel
        title={title}
        required={required}
        labelPlacement={labelPlacement}
      />
      <div
        className={`relative ${
          title && labelPlacement !== "top" ? "w-2/3" : "w-full"
        }`}
      >
        <textarea
          ref={textareaRef}
          id={id}
          type={type}
          name={name}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          minLength={minLength}
          maxLength={maxLength}
          autoComplete={autoComplete}
          spellCheck={spellCheck}
          placeholder={placeholder}
          rows={1}
          className={`${highlightClass} mt-1 p-2 block w-full border outline-none rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-[#696969] ${
            disabled ? "cursor-not-allowed" : "cursor-pointer"
          } ${className} resize-none`}
          style={{
            boxSizing: "border-box",
            lineHeight: "inherit",
            padding: "0.5rem",
            margin: 0,
            maxHeight: "none",
            minHeight: "auto",
            overflow: "auto",
            height: "auto",
          }}
          {...rest}
        />
      </div>
    </div>
  );
};

const BaseInput = ({
  type,
  id,
  name,
  value,
  onChange,
  onBlur,
  disabled,
  className = "",
  highlightClass = "",
  minLength,
  maxLength,
  autoComplete,
  spellCheck,
  placeholder,
  title,
  required,
  labelPlacement = "left",
  ...rest
}) => {
  const handleBlur = (e) => {
    const input = e.target;
    const { value } = input;
    const upperValue = value;
    onChange?.({ target: { ...e.target, value: upperValue } });
    onBlur?.(e);
  };

  const handleChange = (e) => {
    const input = e.target;
    const { value } = input;
    onChange?.({ target: { ...e.target, value: value } });
  };

  return (
    <div
      className={`my-2 relative group ${
        labelPlacement === "top" ? "flex flex-col" : "flex items-center"
      }`}
    >
      <InputLabel
        title={title}
        required={required}
        labelPlacement={labelPlacement}
      />
      <div
        className={`relative ${
          title && labelPlacement !== "top" ? "w-2/3" : "w-full"
        }`}
      >
        <input
          id={id}
          type={type}
          name={name}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          minLength={minLength}
          maxLength={maxLength}
          autoComplete={autoComplete}
          spellCheck={spellCheck}
          placeholder={placeholder}
          className={`${highlightClass} mb-1 px-2 py-2 block w-full outline-none min-h-9 border rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 truncate word-break focus:border-indigo-500 text-[#696969] ${
            disabled ? "cursor-not-allowed" : "cursor-pointer"
          } ${className} break-words whitespace-pre-wrap overflow-wrap-break-word`}
          {...rest}
        />
      </div>
    </div>
  );
};

const PasswordInput = ({
  type,
  id,
  name,
  value,
  onChange,
  onBlur,
  disabled,
  className = "",
  highlightClass = "",
  minLength,
  maxLength,
  autoComplete,
  spellCheck,
  placeholder,
  title,
  required,
  labelPlacement = "left",
  ...rest
}) => {
  return (
    <div
      className={`my-2 relative group ${
        labelPlacement === "top" ? "flex flex-col" : "flex items-center"
      }`}
    >
      <InputLabel
        title={title}
        required={required}
        labelPlacement={labelPlacement}
      />
      <div
        className={`relative ${
          title && labelPlacement !== "top" ? "w-2/3" : "w-full"
        }`}
      >
        <input
          id={id}
          type={type}
          name={name}
          value={value || ""}
          onChange={(e) =>
            onChange({
              target: {
                ...e.target,
                value: e.target.value,
              },
            })
          }
          onBlur={onBlur}
          disabled={disabled}
          minLength={minLength}
          maxLength={maxLength}
          autoComplete={autoComplete}
          spellCheck={spellCheck}
          placeholder={placeholder}
          className={`${highlightClass} mb-1 px-2 py-3 block w-full min-h-9 border rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 truncate word-break focus:border-indigo-500 text-[#696969] ${
            disabled ? "cursor-not-allowed" : "cursor-pointer"
          } ${className} break-words whitespace-pre-wrap overflow-wrap-break-word`}
          {...rest}
        />
      </div>
    </div>
  );
};

const getStartOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const isDateBeforeToday = (date) => {
  const normalized = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
  return normalized < getStartOfToday();
};

const COMPLETE_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const formatManualDateDigits = (numbersOnly) => {
  if (numbersOnly.length <= 4 || numbersOnly[4] === "-") {
    return numbersOnly;
  }
  let formattedValue = `${numbersOnly.slice(0, 4)}-${numbersOnly.slice(4, 6)}`;
  if (numbersOnly.length > 6 && numbersOnly[6] !== "-") {
    formattedValue += `-${numbersOnly.slice(6, 8)}`;
  }
  return formattedValue;
};

const getManualDateValidationError = (formattedValue, validateDateConstraints) => {
  const [year, month, day] = formattedValue.split("-").map(Number);
  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() + 1 !== month ||
    date.getDate() !== day
  ) {
    return "Please enter a valid date";
  }

  return validateDateConstraints(date) || null;
};

// Date Input Component
const DateInput = ({
  name,
  value,
  onChange,
  disabled,
  highlightClass,
  disablePastDates = true,
  title,
  required,
  labelPlacement = "left",
}) => {
  const [startDate, setStartDate] = useState(() =>
    value?.match(/^\d{4}-\d{2}-\d{2}$/)
      ? new Date(
          ...value
            .split("-")
            .map((num, i) => (i === 1 ? Number(num) - 1 : Number(num)))
        )
      : null
  );
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const [isValid, setIsValid] = useState(true);
  const [dateError, setDateError] = useState("");

  useEffect(() => {
    if (value?.match(/^\d{4}-\d{2}-\d{2}$/)) {
      setStartDate(
        new Date(
          ...value
            .split("-")
            .map((num, i) => (i === 1 ? Number(num) - 1 : Number(num)))
        )
      );
      setInputValue(value);
    } else if (value === "") {
      setStartDate(null);
      setInputValue("");
    }
  }, [value]);

  const handleCalendarClick = (e) => {
    if (!disabled) {
      setCalendarOpen((prev) => !prev);
      e.preventDefault();
    }
  };

  const validateDateConstraints = (date) => {
    if (disablePastDates && isDateBeforeToday(date)) {
      return "Past dates are not allowed";
    }
    return "";
  };

  const handleDateChange = (date) => {
    if (date) {
      const constraintError = validateDateConstraints(date);
      if (constraintError) {
        setDateError(constraintError);
      } else {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const formattedDate = `${year}-${month}-${day}`;

        setStartDate(date);
        setInputValue(formattedDate);
        onChange({ target: { name, value: formattedDate } });
        setDateError("");
      }
    } else {
      setStartDate(null);
      setInputValue("");
      onChange({ target: { name, value: "" } });
      setDateError("");
    }
    setCalendarOpen(false);
  };
  const handleManualDateChange = (e) => {
    const value = e.target.value;
    setDateError("");

    if (e.nativeEvent.inputType === "deleteContentBackward") {
      setInputValue(value);
      onChange({ target: { name, value } });
      return;
    }

    const formattedValue = formatManualDateDigits(
      value.replaceAll(/\D/g, "")
    );

    if (formattedValue.length > 10) {
      return;
    }

    setInputValue(formattedValue);

    if (!COMPLETE_DATE_PATTERN.test(formattedValue)) {
      return;
    }

    const validationError = getManualDateValidationError(
      formattedValue,
      validateDateConstraints
    );
    if (validationError) {
      setDateError(validationError);
      return;
    }

    onChange({ target: { name, value: formattedValue } });
  };

  //  const handleManualDateBlur = (e) => {
  //     // If not a complete date, clear the input
  //     if (!/^\d{4}-\d{2}-\d{2}$/.test(inputValue)) {
  //       setInputValue("");
  //       onChange({ target: { name, value: "" } });
  //     }
  //   };

  return (
    <div
      className={`my-2 ${
        labelPlacement === "top" ? "flex flex-col" : "flex items-center"
      }`}
    >
      <InputLabel
        title={title}
        required={required}
        labelPlacement={labelPlacement}
      />
      <div className={`flex flex-col w-full`}>
        <div className="flex border border-gray-300 rounded-md mt-0 p-1 relative h-[42px]">
          <div
            className={`${highlightClass} w-[60px] p-1 h-8 rounded-md bg-gray-100 border-r flex items-center justify-center ${
              disabled ? "cursor-not-allowed" : "cursor-pointer"
            }`}
            onClick={handleCalendarClick}
          >
            <Calendar
              className={disabled ? "text-gray-500" : "text-[#515DD8]"}
            />
          </div>

          <input
            type="text"
            value={inputValue}
            onChange={handleManualDateChange}
            //onBlur={handleManualDateBlur}
            placeholder="YYYY-MM-DD"
            className={`${highlightClass} flex-1 w-full px-2 py-3 h-9 outline-none rounded-r-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-transparent ${
              disabled ? "cursor-not-allowed" : "cursor-pointer"
            } ${isValid ? "" : "border-red-500"}`}
            disabled={disabled}
            maxLength={10}
          />

          {calendarOpen && (
            <div className="absolute left-0 top-full mt-1 z-50">
              <DatePicker
                selected={startDate}
                onChange={handleDateChange}
                inline
                calendarClassName="border border-gray-200 shadow-lg bg-white rounded-md"
                minDate={disablePastDates ? getStartOfToday() : null}
              />
            </div>
          )}
        </div>
        {dateError && (
          <div className="text-red-500 text-sm mt-1">{dateError}</div>
        )}
      </div>
    </div>
  );
};

// Email Input Component
const EmailInput = ({
  name,
  value,
  onChange,
  disabled,
  highlightClass,
  className,
  title,
  required,
  rows = 1,
  charPerRow = 20,
}) => {
  const [emailError, setEmailError] = useState("");
  const [numRows, setNumRows] = useState(rows);
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const textareaRef = useRef(null);

  useEffect(() => {
    let rowsNeeded = Math.max(
      Math.ceil(String(value).length / charPerRow),
      rows
    );
    setNumRows(rowsNeeded === 1 ? rowsNeeded : rowsNeeded + 1);
  }, [value, rows, charPerRow]);

  const handleEmailChange = (e) => {
    const inputValue = e.target.value;
    onChange({ target: { name, value: inputValue } });
  };

  const handleEmailBlur = (e) => {
    if (value) {
      if (emailRegex.test(value)) {
        setEmailError("");
      } else {
        setEmailError("Please enter a valid email");
      }
    } else {
      setEmailError("Please enter an email");
    }
    const input = e.target;
    const { value } = input;
    const upperValue = value;
    onChange({ target: { name, value: upperValue } });
  };

  const resizeTextarea = () => {
    const ref = textareaRef.current;
    if (ref) {
      ref.style.height = "auto";
      ref.style.minHeight = `${ref.scrollHeight}px`;
    }
  };

  useEffect(() => {
    resizeTextarea();
  }, [value]);

  return (
    <div className="flex items-center my-2">
      <InputLabel title={title} required={required} labelPlacement="left" />
      <div
        className={`flex flex-col ${title ? "w-2/3" : "w-full"} mt-1 relative`}
      >
        <textarea
          ref={textareaRef}
          type="email"
          name={name}
          value={value}
          onChange={handleEmailChange}
          onBlur={handleEmailBlur}
          disabled={disabled}
          rows={1}
          className={`${highlightClass} p-2 block w-full outline-none border rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-[#696969] ${className} resize-none`}
          style={{
            boxSizing: "border-box",
            lineHeight: "inherit",
            padding: "0.5rem",
            margin: 0,
            maxHeight: "none",
            minHeight: "auto",
            overflow: "auto",
            height: "auto",
          }}
        />
        {emailError && (
          <span className="text-red-500 text-sm">{emailError}</span>
        )}
      </div>
    </div>
  );
};

// Amount Input Component
const AmountInput = ({
  name,
  value,
  onChange,
  disabled,
  highlightClass,
  title,
  required,
  labelPlacement = "left",
}) => (
  <div
    className={`my-2 ${
      labelPlacement === "top" ? "flex flex-col" : "flex items-center"
    }`}
  >
    <InputLabel
      title={title}
      required={required}
      labelPlacement={labelPlacement}
    />
    <div
      className={`flex ${
        title && labelPlacement !== "top" ? "w-2/3" : "w-full"
      } mt-1 border border-gray-300 rounded-md bg-gray-100 relative`}
    >
      <input
        type="text"
        value="TTD"
        className={`${highlightClass} w-1/5 p-2 h-9 text-center border-r border-gray-300 bg-gray-100 text-[#696969] ${
          disabled ? "cursor-not-allowed" : "cursor-pointer"
        }`}
        data-testid={`input-field-${name}`}
        disabled={disabled}
      />
      <textarea
        name={name}
        value={
          value
            ? `$ ${new Intl.NumberFormat("en-US", {
                style: "decimal",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(value)}`
            : ""
        }
        onChange={(e) => {
          const rawValue = e.target.value
            .replace(/[^0-9]/g, "")
            .replace(/^0+/, "");
          const formattedValue = rawValue
            ? `${rawValue.slice(0, -2) || "0"}.${rawValue
                .slice(-2)
                .padStart(2, "0")}`
            : "";
          onChange({ target: { name, value: formattedValue } });
        }}
        disabled={disabled}
        data-testid={`input-field-${name}`}
        rows={1}
        style={{
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(0,0,0,0.5) transparent",
        }}
        className={`${highlightClass} w-4/5 p-2 min-h-[36px] max-h-24 rounded-r-md shadow-sm overflow-y-auto resize-none break-all focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-transparent text-[#696969] ${
          disabled ? "cursor-not-allowed" : "cursor-pointer"
        } [&::-webkit-scrollbar]:w-[3px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-[2px] [&::-webkit-scrollbar-thumb:hover]:bg-gray-400`}
      />
    </div>
  </div>
);

const Input = ({
  title,
  type = "text",
  name,
  value = "",
  onChange,
  className,
  onBlurChange,
  required = false,
  disabled = false,
  autoComplete,
  spellCheck,
  minLength,
  maxLength,
  disablePastDates = true,
  highlightClass = "",
  labelPlacement = "left",
  placeholder,
  autoFocus,
  onKeyPress,
  onKeyDown,
}) => {
  const commonProps = {
    name,
    value: value || "",
    onChange: onChange, // Pass onChange directly without modification
    onBlur: onBlurChange,
    disabled,
    className,
    highlightClass,
    minLength,
    maxLength,
    autoComplete,
    spellCheck,
    title,
    required,
    labelPlacement,
    placeholder,
    autoFocus,
    onKeyPress,
    onKeyDown,
  };

  // Rest of your switch statement with updated onChange handlers
  switch (type) {
    case "date":
      return (
        <DateInput
          {...commonProps}
          disablePastDates={disablePastDates}
        />
      );
    case "email":
      return <EmailInput {...commonProps} />;
    case "amount":
      return <AmountInput {...commonProps} />;
    case "numeric":
      return (
        <BaseInput
          {...commonProps}
          type="text"
          onChange={(e) =>
            onChange({
              target: { name, value: e.target.value.replace(/[^0-9]/g, "") },
            })
          }
        />
      );
    case "alphanumeric":
      return (
        <BaseInput
          {...commonProps}
          type="text"
          onChange={(e) =>
            onChange({
              target: {
                name,
                value: e.target.value.replace(/[^a-zA-Z0-9 :\/._-]/g, ""),
              },
            })
          }
        />
      );
    case "alpha":
      return (
        <TextAreaInput
          {...commonProps}
          type="text"
          onChange={(e) =>
            onChange({ target: { name, value: e.target.value } })
          }
        />
      );
    case "phoneNumber":
      return (
        <BaseInput
          {...commonProps}
          type="text"
          onChange={(e) =>
            onChange({
              target: { name, value: e.target.value.replace(/[^0-9+\ ]/g, "") },
            })
          }
        />
      );
    case "password":
      return (
        <PasswordInput
          {...commonProps}
          type={type}
          onChange={(e) =>
            onChange({
              target: { name, value: e.target.value },
            })
          }
        />
      );
    case "passwordText":
      return (
        <PasswordInput
          {...commonProps}
          type={type}
          onChange={(e) =>
            onChange({
              target: { name, value: e.target.value },
            })
          }
        />
      );
    case "numericPassword":
      return (
        <PasswordInput
          {...commonProps}
          type="password"
          onChange={(e) =>
            onChange({
              target: { name, value: e.target.value.replace(/[^0-9]/g, "") },
            })
          }
        />
      );
    case "numericPasswordText":
      return (
        <PasswordInput
          {...commonProps}
          type="text"
          onChange={(e) =>
            onChange({
              target: { name, value: e.target.value.replace(/[^0-9]/g, "") },
            })
          }
        />
      );
    case "plainText":
      return (
        <BaseInput
          {...commonProps}
          type="text"
          onChange={(e) =>
            onChange({
              target: { name, value: e.target.value },
            })
          }
        />
      );
    default:
      return (
        <TextAreaInput
          {...commonProps}
          type="text"
          onChange={(e) =>
            onChange({
              target: {
                name,
                value: e.target.value.replace(/[^a-zA-Z0-9 :\/._-]/g, ""),
              },
            })
          }
        />
      );
  }
};

export default Input;
